import { useId, useState } from 'react';
import { PaymentMethod, PaymentMethodLabel, StoreOrder, StoreOrderItem, StoreOrderPaymentMethod } from '../../models';
import { storeService } from '../../services/storeService';
import { Modal } from '../ui/Modal';
import { SearchableSelect, SelectOption } from '../ui/SearchableSelect';
import { safeImageUrl } from '../../utils/url';
import { extractApiError, formatCurrency } from '../../utils/orderFormat';
import { OrderPaymentList } from './OrderPaymentList';

const PAYMENT_OPTIONS: SelectOption[] = Object.entries(PaymentMethodLabel).map(([id, label]) => ({
  id: Number(id),
  label,
}));

interface EditOrderModalProps {
  order: StoreOrder;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

export function EditOrderModal({ order, onClose, onSaved }: EditOrderModalProps) {
  const [items, setItems] = useState<StoreOrderItem[]>(() => order.items.map((i) => ({ ...i })));
  const [payments, setPayments] = useState<StoreOrderPaymentMethod[]>(() => order.paymentMethods.map((p) => ({ ...p })));
  const [observation, setObservation] = useState(order.observation ?? '');
  const [newMethod, setNewMethod] = useState<number>(PaymentMethod.Pix);
  const [newAmountCents, setNewAmountCents] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const amountId = useId();
  const observationId = useId();

  const subtotal = items.reduce((sum, i) => sum + i.totalPrice, 0);
  const newAmountDisplay = newAmountCents === 0 ? '' : formatCurrency(newAmountCents / 100);

  function handleAmountInput(e: React.ChangeEvent<HTMLInputElement>) {
    const digits = e.target.value.replace(/\D/g, '');
    setNewAmountCents(parseInt(digits || '0', 10));
  }

  function addPayment() {
    if (newAmountCents <= 0) return;
    setPayments((prev) => [
      ...prev,
      {
        id: 0,
        paymentMethod: newMethod,
        paymentMethodLabel: PaymentMethodLabel[newMethod as PaymentMethod],
        amountPaid: newAmountCents / 100,
      },
    ]);
    setNewAmountCents(0);
  }

  async function save() {
    if (items.length === 0) {
      setError('O pedido precisa ter pelo menos uma peça. Para remover tudo, cancele o pedido.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await storeService.updateOrder(order.id, {
        observation: observation || undefined,
        items: items.map((i) => ({
          id: i.id,
          productId: i.productId,
          productVariantId: i.productVariantId,
          quantity: i.quantity,
          unitPrice: i.unitPrice,
          totalPrice: i.totalPrice,
        })),
        paymentMethods: payments.map((p) => ({
          id: p.id,
          paymentMethod: p.paymentMethod,
          amountPaid: p.amountPaid,
        })),
      });
      await onSaved();
      onClose();
    } catch (e) {
      setError(extractApiError(e, 'Não foi possível salvar o pedido. Tente de novo.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={saving ? () => undefined : onClose}
      title={`Editar pedido #${order.id}`}
      footer={
        <div>
          {error && (
            <p role="alert" className="text-sm text-danger mb-3">
              {error}
            </p>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 min-h-10 px-4 text-sm font-medium text-text-dark border border-champagne-dark rounded-full hover:border-chocolate transition-colors disabled:opacity-60"
            >
              Descartar
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="flex-1 min-h-10 px-4 text-sm font-semibold text-white bg-chocolate-dark hover:bg-chocolate-deep rounded-full transition-colors disabled:opacity-60"
            >
              {saving ? 'Salvando...' : 'Salvar alterações'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        <section>
          <h3 className="text-sm font-semibold text-text-dark">Peças</h3>
          <p className="text-sm text-text-medium mb-3">Para incluir outras peças, cancele este pedido e faça um novo.</p>
          {items.length === 0 ? (
            <p className="text-sm text-text-medium bg-champagne-light rounded-xl px-3 py-3">Todas as peças foram removidas.</p>
          ) : (
            <ul className="space-y-2">
              {items.map((item, idx) => (
                <li key={`${item.id}-${idx}`} className="flex items-center gap-3 bg-champagne-light rounded-xl px-3 py-2.5">
                  {item.imageUrl ? (
                    <img src={safeImageUrl(item.imageUrl)} alt="" className="w-10 h-10 rounded-lg object-cover bg-champagne shrink-0" />
                  ) : (
                    <span className="w-10 h-10 rounded-lg bg-champagne shrink-0" aria-hidden="true" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-dark truncate">{item.productName}</p>
                    <p className="text-xs text-text-medium">
                      {item.variantLabel ? `${item.variantLabel} · ` : ''}
                      {item.quantity}x {formatCurrency(item.unitPrice)}
                    </p>
                  </div>
                  <span className="hidden sm:inline text-sm font-semibold text-text-dark tabular-nums shrink-0">{formatCurrency(item.totalPrice)}</span>
                  <button
                    type="button"
                    onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                    aria-label={`Remover ${item.productName}`}
                    className="w-9 h-9 rounded-full flex items-center justify-center text-danger hover:bg-danger/10 transition-colors shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 flex justify-between text-sm">
            <span className="text-text-medium">Subtotal</span>
            <span className="font-semibold text-text-dark tabular-nums">{formatCurrency(subtotal)}</span>
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-text-dark">Pagamento</h3>
          <p className="text-sm text-text-medium mb-3">Você pode combinar mais de uma forma de pagamento.</p>
          <OrderPaymentList payments={payments} onRemove={(idx) => setPayments((prev) => prev.filter((_, i) => i !== idx))} />

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_8rem_auto] gap-2 sm:items-end">
            <div>
              <p className="text-sm font-medium text-text-medium mb-1">Forma de pagamento</p>
              <SearchableSelect
                options={PAYMENT_OPTIONS}
                value={newMethod}
                onChange={(id) => id != null && setNewMethod(id)}
                required
              />
            </div>
            <div>
              <label htmlFor={amountId} className="block text-sm font-medium text-text-medium mb-1">
                Valor
              </label>
              <input
                id={amountId}
                type="text"
                inputMode="numeric"
                placeholder="R$ 0,00"
                value={newAmountDisplay}
                onChange={handleAmountInput}
                className="input-field"
              />
            </div>
            <button
              type="button"
              onClick={addPayment}
              disabled={newAmountCents <= 0}
              className="min-h-10 px-4 text-sm font-medium text-chocolate-dark border border-chocolate rounded-full hover:bg-champagne-light transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              Adicionar
            </button>
          </div>
        </section>

        <section>
          <label htmlFor={observationId} className="block text-sm font-semibold text-text-dark mb-2">
            Observação <span className="font-normal text-text-medium">(opcional)</span>
          </label>
          <textarea
            id={observationId}
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            rows={2}
            className="input-field resize-none"
          />
        </section>
      </div>
    </Modal>
  );
}
